import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import EventIcon from '@mui/icons-material/Event'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useSelector } from 'react-redux'
import { ENTITY_TYPES } from '@sales-center/common'
import { ActivitiesContextProvider } from '@user/features/Activity/context'
import { ActivityForm } from '@user/features/Activity/components/ActivityForm'
import { useFetchActivityTypes } from '@user/features/Activity/hooks'
import { selectCurrentUser } from '@user/store/authorization'
import type { ActivityPrefill } from '@user/features/Activity/components/ActivityForm/hooks/useInitValues'
import type { OwnerOption } from '@user/features/Activity/components/ActivityForm/ActivityFormFields.types'
import { AttachToPicker, type AttachedRecord } from './AttachToPicker'
import { OwnerScopeGuard } from './OwnerScopeGuard'
import {
  useCreateEventOwners,
  type OwnerScopeStatus,
} from './useCreateEventOwners'
import { useEditActivity } from './useEditActivity'

// Small coloured badge behind the header icon (shared by create + edit).
const ICON_BADGE_SX = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  borderRadius: 1,
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const

export type EditEntity = {
  entityType: 'Lead' | 'Opportunity'
  entityId: number
}

type Props = {
  open: boolean
  onClose: () => void
  // SH-1408 refetch seam — fired after a successful create. No-op until the
  // calendar feed exists.
  onCreated?: () => void
  // SH-1432: when set, the dialog opens in EDIT mode for this activity — it
  // fetches the full record, pre-fills the form and PUTs on save. Absent => the
  // unchanged create flow.
  editActivityId?: number
  // SH-1432: the activity's parent record. GET /Activities/{id} does not return
  // entityType/entityId, so the host supplies them (from the calendar event) and
  // they are merged over the fetched activity. Absent => an unattached activity.
  editEntity?: EditEntity
  // SH-1432 refetch seam — fired after a successful edit (mirrors onCreated).
  onUpdated?: () => void
  /** SH-2133: seeds date/time when opened from a click on empty grid space. */
  prefill?: ActivityPrefill
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

const OWNER_SCOPE_ERRORS: Record<OwnerScopeStatus, string | undefined> = {
  loading: 'Owners are still loading',
  error: 'Owners could not be loaded',
  ready: undefined,
}

type ContentProps = {
  onClose: () => void
  onCreated?: () => void
  onUpdated?: () => void
  isEdit: boolean
  initialAttached: AttachedRecord | null
  savedOwner: OwnerOption | null
  /** SH-2133: seeds date/time when opened from a click on empty grid space. */
  prefill?: ActivityPrefill
}

function CreateEventModalContent(props: ContentProps) {
  const { onClose, onCreated, onUpdated, isEdit, initialAttached } = props
  const { savedOwner, prefill } = props

  // Load activity types into the ActivitiesContext so the reused TypeSelector
  // has options to render.
  useFetchActivityTypes()
  const { userId } = useSelector(selectCurrentUser)

  const [attached, setAttached] = useState<AttachedRecord | null>(
    initialAttached,
  )
  const {
    salesTeamId,
    owners: scopedOwners,
    allowedOwnerIds,
    status,
  } = useCreateEventOwners(attached)
  const ownerError = OWNER_SCOPE_ERRORS[status]

  const owners = useMemo(() => {
    if (!savedOwner?.id || status !== 'ready') return scopedOwners
    const savedId = savedOwner.id.toLowerCase()
    return scopedOwners.some((o) => o.id?.toLowerCase() === savedId)
      ? scopedOwners
      : [...scopedOwners, savedOwner]
  }, [scopedOwners, savedOwner, status])

  // ActivityForm fires onCreated on BOTH create and update success (it is the
  // form's onSuccess). Route it to the right refetch seam, then close.
  const handleSuccess = useCallback(() => {
    if (isEdit) onUpdated?.()
    else onCreated?.()
    onClose()
  }, [isEdit, onCreated, onUpdated, onClose])

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 2, pb: 0 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={ICON_BADGE_SX}>
            <EventIcon fontSize="small" />
          </Box>
          <Typography variant="h6">
            {isEdit ? 'Edit Event' : 'New Event'}
          </Typography>
        </Stack>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          data-test-id="create-event-modal-close"
        >
          <CloseIcon />
        </IconButton>
      </Stack>
      <DialogContent>
        <ActivityForm
          attachToMode
          ownerId={userId ?? null}
          salesTeamId={salesTeamId}
          owners={owners}
          ownerError={ownerError}
          prefill={prefill}
          attachToSlot={
            <>
              <AttachToPicker disabled={isEdit} onRecordChange={setAttached} />
              <OwnerScopeGuard
                allowedOwnerIds={allowedOwnerIds}
                currentUserId={userId}
                ownerError={ownerError}
                keepOwnerId={savedOwner?.id}
              />
            </>
          }
          onCreated={handleSuccess}
          onCancel={onClose}
        />
      </DialogContent>
    </>
  )
}

/** Identity of a form seed, so a new one always remounts the form. */
function prefillKey(prefill?: ActivityPrefill): string {
  if (!prefill) return 'unseeded'
  return `${prefill.day.getTime()}:${prefill.start?.getTime() ?? 'date-only'}`
}

export function CreateEventModal(props: Props) {
  const {
    open,
    onClose,
    onCreated,
    onUpdated,
    editActivityId,
    editEntity,
    prefill,
  } = props

  // Keying on the seed alone is not enough: cancelling and re-clicking the
  // SAME slot within the dialog's exit transition leaves the key unchanged,
  // so the live Formik instance survives and the discarded draft reappears.
  // Counting opens makes every open a fresh form.
  const [openCount, setOpenCount] = React.useState(0)
  const [wasOpen, setWasOpen] = React.useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setOpenCount((n) => n + 1)
  }
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // The host drops editActivityId the instant it closes, but MUI keeps the
  // content mounted through the exit transition. Reading the prop alone would
  // retitle the closing dialog "New Event" and re-enable Attach-To, so track the
  // id while open and freeze it once closed. Same set-state-during-render
  // pattern as the open counter above.
  const [lastEditActivityId, setLastEditActivityId] =
    React.useState(editActivityId)
  if (open && editActivityId !== lastEditActivityId) {
    setLastEditActivityId(editActivityId)
  }
  const isEdit = (open ? editActivityId : lastEditActivityId) != null

  // Fetch only while open in edit mode; the context seeds current/activities at
  // mount, so the form waits for the record.
  const { activity, status } = useEditActivity(
    open && isEdit ? editActivityId : undefined,
  )

  // Load error: fetchActivity has already toasted — just close.
  useEffect(() => {
    if (status === 'failed') onClose()
  }, [status, onClose])

  // Edit mode renders the form only once the record has loaded. Gated on `open`
  // because the fetch state resets to idle on close, which would otherwise flash
  // the spinner over the frozen edit dialog during its exit transition.
  const showLoading = open && isEdit && status !== 'loaded'

  const editAttached = useMemo<AttachedRecord | null>(
    () =>
      activity && editEntity
        ? {
            salesTeamId: activity.salesTeamId > 0 ? activity.salesTeamId : null,
            entityType: ENTITY_TYPES[editEntity.entityType],
          }
        : null,
    [activity, editEntity],
  )

  const savedOwner = useMemo<OwnerOption | null>(
    () =>
      activity?.ownerId && activity.ownerId !== EMPTY_GUID
        ? { id: activity.ownerId, name: activity.owner }
        : null,
    [activity],
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      data-test-id="create-event-modal"
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px' } }}
    >
      {showLoading ? (
        <Box
          sx={{ p: 4, display: 'flex', justifyContent: 'center' }}
          data-test-id="create-event-modal-loading"
        >
          <CircularProgress />
        </Box>
      ) : (
        <ActivitiesContextProvider
          initialCurrent={activity?.id}
          initialActivities={
            activity ? [{ ...activity, ...editEntity }] : undefined
          }
        >
          <CreateEventModalContent
            // MUI keeps Dialog children mounted through the ~195ms exit
            // transition, so reopening inside that window reuses the live
            // Formik instance and its already-consumed initialValues. Keying
            // on the seed forces a fresh form, otherwise clicking slot A,
            // closing and quickly clicking slot B would still show slot A.
            key={`${openCount}:${prefillKey(prefill)}`}
            onClose={onClose}
            onCreated={onCreated}
            onUpdated={onUpdated}
            isEdit={isEdit}
            initialAttached={editAttached}
            savedOwner={savedOwner}
            prefill={prefill}
          />
        </ActivitiesContextProvider>
      )}
    </Dialog>
  )
}
CreateEventModal.displayName = 'CreateEventModal'
